<template>
  <div class="path-card d-flex" style="flex-direction: column; max-height: 100%; flex-grow: 0;">
  <div class="mb-8">
    <ui-radio-group 
      class="mt-16" 
      styleType="inline-values" 
      v-model="redirectMode" 
      :label="`${t('Redirect mode')} ${t(type)}`"
      :labelHelp="type==='landings' && !noDirect && t('If landing page is switched off in a flow, then previously selected redirect mode will not work. Instead the redirect mode of an offer will be applied.')"
    >
      <ui-radio
        value="302"
        label="302"
      ></ui-radio>
      <ui-radio
        value="meta"
        :label="t('Meta')"
        class="ml-16"
      ></ui-radio>

      <div :class="{'plan-limits-warning__doubleMeta': showPlanLimitsWarningDoubleMeta}">
        <ui-tooltip-icon
          class="plan-limits-warning__tooltip"
          preset="error"
          v-if="showPlanLimitsWarningDoubleMeta"
          :text="t('This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.')"
        ></ui-tooltip-icon>
        <ui-radio
          value="doubleMeta"
          :plan-limits="'doubleMetaRedirect'"
          class="ml-16 no-wrap"
          :label="t('Double meta')"
        ></ui-radio>
      </div>
      
    </ui-radio-group>
    
    <ui-checkbox
      v-if="redirectMode !== '302'"
      :label="retargetingLabel"
      v-model="retargeting"
    >
    </ui-checkbox>
  </div>

	<div style="flex-grow: 0;">
    <ui-select
      v-if="redirectMode === 'doubleMeta'"
      v-model="redirectDomainId"
      :placeholder="t('Account default')"
      :label="t('Redirect domain')"
      :items="store.redirectDomainsWithAccountDefault"
      item-text="domain"
      item-value="id"
      :return-object="false"
      :hide-details="2"
      attach
    ></ui-select>
	</div>

	<div v-if="(redirectMode === 'doubleMeta' || redirectMode === 'meta') && retargeting">
		<ui-textarea
      v-model="retargetingValue"
      rows="2"
      :hide-details="2"
    >
      <template #label>
        <label class="font-weight-bold text-capitalize">
          {{ t('Retargeting pixel script') }} ({{ t(type) }})
        </label>
      </template>
    </ui-textarea>
	</div>

	<div class="table-popup" style="flex-grow: 1; display: flex; flex-direction: column; flex-shrink: 1; max-height: 100%;">
		<div class="table-popup__header" style="flex-grow: 0;">
			<div class="table-popup__row-header">
				<div class="table-popup__row-header--left">
					<span v-if="type !== 'landings'" class="table-popup__label">
            {{pathCardTitle}}
          </span>
          <ui-icon
            v-if="pathError && noDirect && type !== 'landings'"
            size="12"
            :color="$colors.red"
            class="ml-8"
            icon="fas fa-exclamation-triangle"
          ></ui-icon>
          <ui-switcher v-if="type === 'landings'" class="mr-8" v-model="noDirect">
            <template #label>
              <span class="table-popup__label">
                {{pathCardTitle}}
              </span>
              <ui-icon
                v-if="pathError && noDirect && type !== 'landings'"
                size="12"
                :color="$colors.red"
                class="ml-8"
                icon="fas fa-exclamation-triangle"
              ></ui-icon>
            </template>
          </ui-switcher>
				</div>
				<div class="table-popup__row-header--right">
					<span class="table-popup__label" v-if="noDirect">{{t('Weight')}}</span>
				</div>
			</div>
		</div>
		<div class="table-popup__body" style="flex-grow: 1; flex-shrink: 1; min-height: 44px;">
			<div v-if="!selections.length && noDirect" class="table-popup__row table-popup__row-noland" style="text-align: center">
				<span style="text-align: center; display: block; width: 100%;">
          {{type === 'landings'
            ? t('No landings added')
            : t('No offers added')}}
        </span>
			</div>

      <div v-if="!noDirect" class="table-popup__row table-popup__row-noland" style="text-align: center">
				<span style="text-align: center; display: block; width: 100%;">{{t('Direct linking enabled')}}</span>
			</div>
      <template v-if="noDirect">
        <template v-if="type === 'landings'">
          <recycle-scroller
            v-if="path.landings.length > virtualScrollBreakpoint"
            :items="path.landings"
            :item-size="38"
            :key-field="null"
            :simple-array="true"
            style="max-height: 100%"
          >
            <template v-slot="{ item, index }">
              <create-campaign-dialog-flow-landing
                :key="item"
                :rule-index="ruleIndex"
                :path-index="pathIndex"
                :path="path"
                :landing-index="index"
                :landing="item"
                :form="form"
                :direct-linking="false"
                :current-offers-count="currentOffersCount"
                :errors-raw="errorsRaw"
                :total-weight="totalWeightOfLandings"
                @fix-landing-count="fixLandingCount"
                @delete="delLanding(index)"
                @edit="editLanding(item.id)"
                @preview="previewLanding"
                :landingOptions="landingOptions"
              ></create-campaign-dialog-flow-landing>
            </template>
          </recycle-scroller>

          <div v-else class="path-card__item-list">
            <create-campaign-dialog-flow-landing
              v-for="(landing, landingIndex) in path.landings"
              :key="landing"
              :rule-index="ruleIndex"
              :path-index="pathIndex"
              :path="path"
              :landing-index="landingIndex"
              :landing="landing"
              :form="form"
              :direct-linking="false"
              :current-offers-count="currentOffersCount"
              :total-weight="totalWeightOfLandings"
              :errors-raw="errorsRaw"
              @fix-landing-count="fixLandingCount"
              @delete="delLanding(landingIndex)"
              @edit="editLanding(landing.id)"
              @preview="previewLanding"
              :landingOptions="landingOptions"
            ></create-campaign-dialog-flow-landing>
          </div>
        </template>

        <template v-if="type === 'offers'">
          <recycle-scroller
            v-if="path.offers.length > virtualScrollBreakpoint"
            :items="path.offers"
            :item-size="38"
            :key-field="null"
            :simple-array="true"
            style="max-height: 100%"
          >
            <template v-slot="{ item, index }">
              <create-campaign-dialog-flow-offer
                :key="index"
                :rule-index="ruleIndex"
                :path-index="pathIndex"
                :path="path"
                :offer-index="index"
                :offer="item"
                :offers="path.offers"
                :form="form"
                :offers-count="offersCount"
                :total-weight="totalWeightOfOffers"
                :errors-raw="errorsRaw"
                @delete="delOffer(index)"
                @edit="editOffer(item.id)"
                @preview="previewOffer"
                :offerOptions="offerOptions"
              ></create-campaign-dialog-flow-offer>
            </template>
          </recycle-scroller>
          <div v-else class="path-card__item-list">
            <create-campaign-dialog-flow-offer
              v-for="(offer, offerIndex) in path.offers"
              :key="offerIndex"
              :rule-index="ruleIndex"
              :path-index="pathIndex"
              :offer-index="offerIndex"
              :offer="offer"
              :path="path"
              :form="form"
              :offers-count="offersCount"
              :total-weight="totalWeightOfOffers"
              :errors-raw="errorsRaw"
              @delete="delOffer(offerIndex)"
              @edit="editOffer(offer.id)"
              @preview="previewOffer"
              :offerOptions="offerOptions"
            ></create-campaign-dialog-flow-offer>
          </div>
        </template>
      </template>
		</div>
		<div class="table-popup__footer" style="flex-grow: 0;">
			<div class="table-popup__btn-wr" :class="{disabled:!noDirect}">
        <ui-button-multi
          class="create-flow__button-multi"
          button-style="accent"
          :menu-list="addMenuList"
          icon="fas fa-plus"
        ></ui-button-multi>
			</div>
		</div>
	</div>
  </div>
</template>

<script setup lang="ts">
import { useColors } from '@/composables';
import CreateCampaignDialogFlowLanding from '@/views/reports/forms/campaign/components/create-campaign-dialog-flow-landing.vue';
import CreateCampaignDialogFlowOffer from '@/views/reports/forms/campaign/components/create-campaign-dialog-flow-offer.vue';
import { useFlowPathCard } from './useFlowPathCard';

const $colors = useColors();

const props = defineProps<{
  type: string;
  path: {
    landings: Array<{ id: string; weight?: number; offers?: number }>;
    offers: Array<{ id: string; weight: number }>;
    directLinking?: boolean;
    redirectMode?: string;
    redirectModeOffers?: string;
    retargetingLandings?: string;
    retargetingOffers?: string;
    redirectDomainId?: string;
    redirectDomainIdOffers?: string;
  };
  form: Record<string, unknown>;
  errorsRaw: unknown[][] | null;
  pathError: boolean;
  allWorkspaceIds: unknown[];
  offerOptions: Offer[];
  landingOptions: Landing[];
  ruleIndex: number;
  pathIndex: number;
  isDirectLinking?: boolean;
}>();

const {
  t,
  store,
  virtualScrollBreakpoint,
  redirectMode,
  retargeting,
  noDirect,
  retargetingValue,
  redirectDomainId,
  selections,
  currentOffersCount,
  offersCount,
  pathCardTitle,
  retargetingLabel,
  showPlanLimitsWarningDoubleMeta,
  totalWeightOfLandings,
  totalWeightOfOffers,
  addMenuList,
  fixLandingCount,
  delLanding,
  editLanding,
  previewLanding,
  delOffer,
  editOffer,
  previewOffer,
} = useFlowPathCard(props);
</script>


<style lang="scss" scoped>
.plan-limits-warning__doubleMeta{
  position: relative;
  padding-right: 38px;
  &:before{
    display:block;
    content:'';
    border:1px solid var(--color-red);
    border-radius: 2px;
    position: absolute;
    top: -3px;
    left: 8px;
    right: 0;
    bottom: 6px;
  } 
  .plan-limits-warning__tooltip{
    right: 6px;
    top: -1px;
  }
}
</style>

<style lang="scss">
  .path-card {
    &__item-list {
      max-height: 100%;
    }

    .table-popup__wrap-icon .fas.fa-pencil-alt:hover {
      color: var(--color-green) !important;
      cursor: pointer;
    }

    .table-popup__wrap-icon .fas.fa-external-link:hover {
      color: var(--color-green) !important;
      cursor: pointer;
    }

    .table-popup__wrap-icon .fas.fa-trash-alt:hover {
      color: var(--color-red) !important;
      cursor: pointer;
    }

    .v-text-field__details {
      display: none;
    }

    &__weigth {
      margin-top: 1px;
    }
  }

  .div-table__row {
    background: var(--color-white);
    margin: 2px 0;
    padding: 12px 10px;
    display: flex;
    justify-content: center;
    align-items: center;

    &--select {
      padding: 6px 10px 7px 10px;

      .flow-row-select {
        flex: 1 1 auto;
        min-width: 0;

        .ui-select__wrapper,
        .ui-select {
          width: 100%;
        }

        .ui-select .v-field {
          --v-input-control-height: 32px;
          --v-field-padding-top: 0;
          --v-field-padding-bottom: 0;
          height: 32px;
          min-height: 32px;
        }

        .ui-select .v-field__input {
          min-height: 32px;
        }
      }
    }

    &--header {
      justify-content: space-between;
      padding: 12px 10px;
    }

    .v-select.v-text-field .v-input__slot .v-input__append-inner {
      top: 0;
    }
  }

  .weight-input {
    min-width: 60px;
    max-width: 80px;
    margin-right: 0;
    margin-left: 12px;
    padding-top: 0 !important;
  }

  .table-popup__wrap-icon {
    display: flex;
    min-width: 60px;
    justify-content: center;

    .ui-icon {
      margin-left: 2px;
      margin-right: 2px;
    }
  }

.table-popup{
  margin-top: 10px;
  box-shadow: 0 2px 4px var(--color-black10);
  margin-bottom: 4px;

  &__header{ 
    border-bottom: 2px solid var(--color-gray10);
  }

  &__row-header{
    padding-left: 10px;
    padding-right: 10px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &--left{ 
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }

    &--right {
      width: 170px;
    }
  }
  &__label{
    font-weight: 700;
    text-transform: uppercase;
  }
  &__body{

  }
  &__row{
    border-bottom: 1px solid var(--color-gray10);
    height: 50px;
    display: flex;
    align-items: center;
    &--left{
      padding-right: 12px;
      display: flex;
      align-items: center;
      width: 70%;
      padding-left: 10px;
    }
    &--right{ 
      display: flex; 
      justify-content: space-between;
      .table-popup__wrap-icon{
        display: flex;
        min-width: 60px;
        justify-content: center;
        .fas.fa-edit:hover{
          color: var(--color-green) !important;
          cursor: pointer;
        }
        .fas.fa-external-link:hover {
          color: var(--color-green) !important;
          cursor: pointer;
        }
        .fas.fa-trash-alt:hover{
          color: var(--color-red) !important;
          cursor: pointer;
        }
      }
    }
  }
  &__count{
    padding-right: 10px;
  }
  &__select-source{
    .v-icon{
      margin-top: -16px !important;
    }
    input{
      margin-top: 0 !important;
    }
  }
  &__input-weight{
    display: flex;
    
    input{
      text-align: center;
      height: 32px;
      width: 55px;
      min-width: 60px;
      max-width: 60px;
      padding: 4px 8px 0 !important;
      border: 1px solid var(--color-gray20);
      border-radius: 6px 0 0 6px !important;

      &:focus{
        outline: none;
      }
    }
    span{
      border: 1px solid var(--color-gray20);
      border-left: none;
      border-radius: 0 5px 5px 0; 
      text-align: center; 
      min-width: 50px;
      max-width: 50px;
      display: flex;
      height: 32px;
      align-items: center;
      justify-content: center;

      &.disabled {
        border: 1px solid var(--color-gray10) !important;
        color: var(--color-gray40) !important;
        border-left: none!important;
        pointer-events: none;
      }
    }
    
    .path-card & .v-text-field input {
      padding-top: 0 !important;
    }
    
  }

  &__footer{
		.table-popup__btn-wr.disabled{
			.v-btn{
				pointer-events: none;
				background: var(--color-gray10) !important;
				color: var(--color-gray40) !important;
				border: solid 1px var(--color-gray20) !important;
			}
			.table-popup__btn-drop-wr{
				pointer-events: none;
			}
		}
  }
  &__btn-wr{
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 10px;

    button{
      &.primary, &.v-btn--disabled {
        margin: 0;
      }

      &.v-btn--disabled {
        border-color: var(--color-gray20) !important;
      }
    }
    button.first-btn-pop{
      &.primary, &.v-btn--disabled {
        border-top-right-radius: 0 ;
        border-bottom-right-radius: 0 ;
        border-right: 1px solid var(--color-gray20) !important;
      }
    }
  }
  &__btn-drop{
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;

	}
}

</style>

